/**
 * Marketplace-style home page variants (iter56c).
 *
 * Common chrome (shared header across all 3 themes):
 *   Row 1 — Deliver-to address (left) + notifications bell (right)
 *   Row 2 — full-width search bar
 *   Row 3 — variant-specific accent strip:
 *     • Amazon   : navy ALL / TODAY'S DEALS / COUPONS / YOUR ORDERS / REFER
 *     • Flipkart : tenant brand wordmark
 *   (Blinkit has its own page — BlinkitHome.jsx.)
 *
 * Below the header:
 *   1. Verticals strip with proper lucide icons (BizCategoryIcon).
 *   2. Banner carousel.
 *   3. Coupon strip.
 *   4. Dynamic product sections — laid out as a 2-column grid
 *      (single row of horizontal cards replaced) capped at 6 items
 *      with a "See all" link to the search page for the rest.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Search, ChevronRight, MapPin, Bell, Plus, ShoppingBag,
    Sparkles, Percent, Zap, Truck,
} from "lucide-react";
import { api } from "../lib/api";
import { poweredByBrand } from "../lib/utils";
import { useAddProductToCart } from "../lib/useAddProductToCart";
import { useCartCount } from "../lib/useCartCount";
import AddressPickerModal from "./AddressPickerModal";
import BannerCarousel from "./BannerCarousel";
import StockBadge from "./StockBadge";
import BizCategoryIcon from "./BizCategoryIcon";
import RoleSwitcher from "./RoleSwitcher";
import { CardSkeleton } from "./Loaders";
import GroupCategoryChipRow from "./GroupCategoryChipRow";
import BlinkitHome from "./BlinkitHome";
import { useDefaultAddress, useUnreadNotifCount } from "../lib/storefrontHooks";
import LseHome from "./lse/LseHome";

/* ───────────────── Shared chrome ───────────────── */

function IconBadgeButton({ to, label, count, dot, children, testid }) {
    const showCount = !dot && count > 0;
    const showDot = !!dot && count > 0;
    return (
        <Link to={to} aria-label={label} data-testid={testid}
              className="p-2 rounded-full hover:bg-white/10 transition relative shrink-0">
            {children}
            {showCount && (
                <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[10px] font-extrabold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                      data-testid={`${testid}-count`}>
                    {count > 9 ? "9+" : count}
                </span>
            )}
            {showDot && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white/30"
                      data-testid={`${testid}-dot`}
                      aria-label={`${count} unread`} />
            )}
        </Link>
    );
}

/** Unified location-row used by all 3 themes — left: address, right: bell + cart. */
function LocationBellRow() {
    const cartCount = useCartCount();
    const unreadNotifs = useUnreadNotifCount();
    const addr = useDefaultAddress();
    const [picking, setPicking] = useState(false);
    const summary = addr
        ? [addr.line1, addr.line2, addr.city].filter(Boolean).join(", ")
        : "Select your address";
    return (
        <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
            <button
                type="button"
                onClick={() => setPicking(true)}
                className="flex-1 min-w-0 flex items-center gap-1.5 text-xs text-left hover:opacity-90"
                data-testid="mp-deliver-to">
                <MapPin size={14} className="shrink-0" />
                <span className="min-w-0">
                    <span className="block text-[10px] uppercase tracking-wider opacity-70 font-bold">
                        Deliver to{addr?.label ? ` · ${addr.label}` : ""}
                    </span>
                    <span className="block text-sm font-bold truncate">{summary}</span>
                </span>
                <ChevronRight size={14} className="opacity-70" />
            </button>
            <div className="flex items-center gap-0.5 -mr-1">
                {/* iter66 — top-nav role switcher (hidden for guests + single-role users) */}
                <RoleSwitcher className="mr-1" />
                <IconBadgeButton to="/notifications" label="Notifications" count={unreadNotifs} dot testid="mp-bell">
                    <Bell size={20} />
                </IconBadgeButton>
                <IconBadgeButton to="/cart" label="Your cart" count={cartCount} testid="mp-cart">
                    <ShoppingBag size={20} />
                </IconBadgeButton>
            </div>
            {picking && (
                <AddressPickerModal
                    initial={addr || null}
                    onClose={() => setPicking(false)}
                    onSaved={() => {
                        setPicking(false);
                        window.dispatchEvent(new Event("addresses:update"));
                    }}
                />
            )}
        </div>
    );
}

function AmazonHeader({ tenantName }) {
    return (
        <div className="bg-accent text-accent-foreground sticky top-0 z-40 shadow-md" data-testid="mp-header-amazon">
            <LocationBellRow />
            <div className="px-4 pb-2">
                <Link to="/search" className="flex items-center gap-2 bg-card text-foreground rounded-md px-3 py-2 shadow-sm">
                    <Search size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground flex-1 truncate">
                        Search {tenantName || "marketplace"}
                    </span>
                </Link>
            </div>
            <div className="bg-foreground/10 px-4 py-1.5 flex gap-4 overflow-x-auto no-scrollbar whitespace-nowrap text-[11px] font-bold uppercase tracking-wider">
                <Link to="/search">All</Link>
                <Link to="/search?vertical=food">Today&apos;s deals</Link>
                <Link to="/store-coupons">Coupons</Link>
                <Link to="/orders">Your orders</Link>
                <Link to="/refer">Refer</Link>
            </div>
        </div>
    );
}

function FlipkartHeader({ tenantName }) {
    return (
        <div className="bg-primary text-primary-foreground sticky top-0 z-40 shadow-md" data-testid="mp-header-flipkart">
            <LocationBellRow />
            <div className="px-4 pb-3">
                <Link to="/" className="block font-extrabold italic tracking-tight text-base mb-2">
                    {tenantName || "Storefront"}
                </Link>
                <Link to="/search" className="flex items-center gap-2 bg-card text-foreground rounded-sm px-3 py-2.5">
                    <Search size={18} className="text-primary" />
                    <span className="text-sm text-muted-foreground flex-1 truncate">Search for products, brands and more</span>
                </Link>
            </div>
        </div>
    );
}

function VariantHeader({ variant, tenantName }) {
    if (variant === "flipkart") return <FlipkartHeader tenantName={tenantName} />;
    return <AmazonHeader tenantName={tenantName} />;
}

/* ───────────────── Verticals strip ───────────────── */

function VerticalsRail({ verticals, variant }) {
    if (!verticals?.length) return null;
    const tileSize = variant === "flipkart" ? 64 : 56;
    const roundedCls = variant === "flipkart" ? "rounded-full" : "rounded-lg";
    return (
        <section className="bg-card border-b border-border px-3 py-3 overflow-x-auto no-scrollbar flex gap-3"
                 data-testid={`mp-verticals-${variant}`}>
            {verticals.slice(0, 14).map((v) => (
                <Link key={v.id || v.slug} to={`/search?vertical=${v.slug}`}
                      className="flex flex-col items-center w-16 shrink-0 active:scale-[0.96] transition"
                      data-testid={`mp-vertical-${v.slug}`}>
                    <div className={`bg-muted border border-border ${roundedCls} flex items-center justify-center mb-1 overflow-hidden`}
                         style={{ width: tileSize, height: tileSize }}>
                        {v.icon_url ? (
                            <img src={v.icon_url} alt="" className="w-3/4 h-3/4 object-contain" />
                        ) : (
                            <BizCategoryIcon name={v.icon} size={26} className="text-primary" />
                        )}
                    </div>
                    <span className="text-[10px] font-bold text-center leading-tight line-clamp-2">
                        {v.name || v.label || v.slug}
                    </span>
                </Link>
            ))}
        </section>
    );
}

/* ───────────────── Coupon strip ───────────────── */

function CouponStrip({ coupons, variant }) {
    if (!coupons?.length) return null;
    if (variant === "amazon") {
        return (
            <section className="mt-3 bg-card mx-3 rounded-md shadow-sm">
                <div className="bg-primary text-primary-foreground px-3 py-2 flex items-center gap-2 rounded-t-md">
                    <Zap size={14} /> <span className="text-xs font-bold uppercase tracking-wider">Today&apos;s Deals</span>
                </div>
                <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {coupons.slice(0, 6).map((c) => (
                        <Link key={c.id} to="/store-coupons" data-testid={`mp-coupon-${c.code}`}
                              className="border border-dashed border-primary/50 rounded-md p-2.5">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-primary">{c.code}</p>
                            <p className="text-xs font-bold mt-1 line-clamp-2 leading-tight">{c.title}</p>
                        </Link>
                    ))}
                </div>
            </section>
        );
    }
    if (variant === "flipkart") {
        return (
            <section className="px-4 mt-4" data-testid="mp-coupon-ribbon">
                <div className="bg-accent text-accent-foreground rounded-lg px-3 py-2 inline-flex items-center gap-2 font-extrabold text-xs uppercase tracking-wider">
                    <Sparkles size={14} /> Flash Sale · Limited Time
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                    {coupons.slice(0, 4).map((c) => (
                        <Link key={c.id} to="/store-coupons" data-testid={`mp-coupon-${c.code}`}
                              className="bg-card border border-border rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2">
                                <Percent size={14} className="text-accent" />
                                <p className="text-[10px] uppercase tracking-wider font-bold text-primary">{c.code}</p>
                            </div>
                            <p className="text-sm font-bold mt-1 line-clamp-1">{c.title}</p>
                            <p className="text-[11px] text-muted-foreground line-clamp-2">{c.description}</p>
                        </Link>
                    ))}
                </div>
            </section>
        );
    }
    return null;
}

/* ───────────────── Product cards (one per variant) ───────────────── */

function calcDiscount(p) {
    if (!p?.mrp || !p?.selling_price || p.mrp <= p.selling_price) return 0;
    return Math.round(((p.mrp - p.selling_price) / p.mrp) * 100);
}

function inr(n) {
    return `₹${Math.round(Number(n || 0))}`;
}

function ProductCardAmazon({ p, onAdd, busy }) {
    const off = calcDiscount(p);
    return (
        <div className="bg-card rounded-md border border-border overflow-hidden flex flex-col"
             data-testid={`mp-product-${p.id}`} data-shop-id={p.restaurant_id}>
            {/* The card opens the product itself (same route as every other product card); the shop is the
                "from <shop>" link below. It used to open /restaurant/<shop>?product=<id>, which nothing read. */}
            <Link to={`/product/${p.id}`} data-testid={`mp-product-link-${p.id}`}>
                <div className="aspect-square bg-muted">
                    {(p.images || [])[0] && (
                        <img src={p.images[0]} alt={p.name}
                             className="w-full h-full object-cover" loading="lazy" />
                    )}
                </div>
            </Link>
            <div className="p-2 flex-1 flex flex-col">
                <Link to={`/product/${p.id}`} className="block" data-testid={`mp-product-name-link-${p.id}`}>
                    <p className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2rem]">{p.name}</p>
                </Link>
                {p.shop_name && (
                    <Link to={`/restaurant/${p.restaurant_id}`}
                          className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5 hover:text-primary"
                          data-testid={`mp-shop-link-${p.id}`}>
                        from {p.shop_name}
                    </Link>
                )}
                <div className="flex items-baseline gap-1.5 mt-1 flex-wrap">
                    <span className="text-sm font-extrabold text-accent">{inr(p.selling_price)}</span>
                    {off > 0 && <span className="text-[10px] text-muted-foreground line-through">{inr(p.mrp)}</span>}
                    {off > 0 && <span className="text-[10px] font-bold text-accent">{off}% off</span>}
                </div>
                <div className="mt-1"><StockBadge product={p} size="xs" /></div>
                <button onClick={() => onAdd(p)} disabled={busy} data-testid={`mp-add-${p.id}`}
                    className="mt-2 bg-primary text-primary-foreground rounded text-xs font-bold py-1.5 active:scale-95 transition disabled:opacity-50">
                    <Plus size={12} className="inline -mt-0.5" /> Add
                </button>
            </div>
        </div>
    );
}

function ProductCardFlipkart({ p, onAdd, busy }) {
    const off = calcDiscount(p);
    return (
        <div className="bg-card rounded-lg border border-border overflow-hidden flex flex-col"
             data-testid={`mp-product-${p.id}`} data-shop-id={p.restaurant_id}>
            <Link to={`/product/${p.id}`} data-testid={`mp-product-link-${p.id}`}>
                <div className="aspect-[4/5] bg-card relative">
                    {(p.images || [])[0] && (
                        <img src={p.images[0]} alt={p.name}
                             className="w-full h-full object-contain p-2" loading="lazy" />
                    )}
                    {off > 0 && (
                        <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{off}% OFF</span>
                    )}
                </div>
            </Link>
            <div className="p-2 flex-1 flex flex-col">
                <Link to={`/product/${p.id}`} className="block" data-testid={`mp-product-name-link-${p.id}`}>
                    <p className="text-xs font-bold leading-tight line-clamp-2 min-h-[2rem]">{p.name}</p>
                </Link>
                <div className="flex items-baseline gap-1.5 mt-1 flex-wrap">
                    <span className="text-sm font-extrabold">{inr(p.selling_price)}</span>
                    {off > 0 && <span className="text-[10px] text-muted-foreground line-through">{inr(p.mrp)}</span>}
                </div>
                <div className="mt-1"><StockBadge product={p} size="xs" /></div>
                {p.shop_name && (
                    <Link to={`/restaurant/${p.restaurant_id}`}
                          className="text-[10px] text-muted-foreground mt-0.5 hover:text-primary line-clamp-1"
                          data-testid={`mp-shop-link-${p.id}`}>
                        {p.shop_name}
                    </Link>
                )}
                <button onClick={() => onAdd(p)} disabled={busy} data-testid={`mp-add-${p.id}`}
                    className="mt-2 bg-primary text-primary-foreground rounded text-xs font-bold py-1.5 active:scale-95 transition disabled:opacity-50">
                    <Plus size={12} className="inline -mt-0.5" /> Add to cart
                </button>
            </div>
        </div>
    );
}

/* ───────────────── Section row — horizontal scroll with 2 (or 3) stacked rows ─────────────────
 *
 * Layout: a CSS grid with ``grid-auto-flow: column`` so cards fill the
 * first column top→bottom, then the next, etc. We pick 2 rows when a
 * section has up to 8 products and 3 rows for larger sections — this
 * keeps everything visually compact while still giving large catalogs
 * a dense feel. The whole thing is `overflow-x: auto` so users get a
 * Blinkit-style horizontal swipe within each section.
 */

function SectionRow({ section, variant }) {
    const { add, busy } = useAddProductToCart();
    const Card = variant === "flipkart" ? ProductCardFlipkart : ProductCardAmazon;

    if (!section.products?.length) return null;

    const products = section.products;
    const rows = products.length > 8 ? 3 : 2;
    // Slight width tweak per variant so each row has a balanced look.
    const cardWidth = variant === "flipkart" ? 156 : 148;

    const wrapperCls = variant === "amazon"
        ? "mt-3 bg-card mx-3 rounded-md shadow-sm p-3"
        : "mt-3 px-3";

    return (
        <section className={wrapperCls} data-testid={`mp-section-${section.slug}`}>
            <div className="flex items-center justify-between pb-2">
                <div className="min-w-0">
                    <h2 className="text-base font-bold leading-tight">
                        {section.title}
                    </h2>
                    {section.subtitle && (
                        <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{section.subtitle}</p>
                    )}
                </div>
                <Link to="/search" className="text-xs font-bold text-primary inline-flex items-center shrink-0">
                    See all <ChevronRight size={12} />
                </Link>
            </div>
            <div className="overflow-x-auto no-scrollbar -mx-1 px-1">
                <div
                    className="grid gap-2.5"
                    style={{
                        gridTemplateRows: `repeat(${rows}, auto)`,
                        gridAutoFlow: "column",
                        gridAutoColumns: `${cardWidth}px`,
                    }}
                    data-testid={`mp-section-grid-${section.slug}`}
                    data-rows={rows}
                >
                    {products.map((p) => (
                        <Card key={p.id} p={p} onAdd={add} busy={busy} />
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ───────────────── Footer / Empty / Container ───────────────── */

function FooterTag({ tenant }) {
    return (
        <footer className="text-center py-8" data-testid="mp-footer">
            <p className="text-sm font-semibold text-foreground/80">{tenant?.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
                Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}
            </p>
        </footer>
    );
}

function EmptySectionsHint() {
    const nav = useNavigate();
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-6 m-3 text-center"
             data-testid="mp-empty-sections">
            <Truck size={28} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-bold mb-1">No product sections yet</p>
            <p className="text-xs text-muted-foreground mb-3">
                Your store admin can create &quot;Deal of the Day&quot;, &quot;Featured Products&quot; and more from
                <span className="font-semibold"> Admin → Home Sections</span>.
            </p>
            {/* Opens the product catalogue (the Catalog tab), like the Blinkit home's button — it used to open Search. */}
            <button onClick={() => nav("/products")} data-testid="mp-empty-browse"
                className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-xs font-semibold">
                Browse all products
            </button>
        </div>
    );
}

/**
 * The ONE place a marketplace ``homeLayout`` becomes a page. Both storefront entry points — MainPage (``/``)
 * and HomePage (``/food``) — render this, so every route picks the same layout. Blinkit has its own
 * quick-commerce page; Amazon and Flipkart share the layout below.
 */
export default function MarketplaceHome(props) {
    if (props.variant === "blinkit") {
        return <BlinkitHome feed={props.feed} verticals={props.verticals} tenant={props.tenant} />;
    }
    return <ClassicMarketplaceHome {...props} />;
}

function ClassicMarketplaceHome({ variant, feed, verticals, tenant }) {
    const [sections, setSections] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/marketplace/home-sections", { params: { variant } })
            .then(({ data }) => { if (alive) setSections(data || []); })
            .catch(() => { if (alive) setSections([]); });
        return () => { alive = false; };
    }, [variant]);

    return (
        <div className="pb-32" data-testid="marketplace-home" data-variant={variant}>
            <VariantHeader variant={variant} tenantName={tenant?.name} />
            <VerticalsRail verticals={verticals} variant={variant} />

            <div className={variant === "flipkart" ? "px-4 mt-3" : "mx-3 mt-3"}>
                <BannerCarousel banners={feed?.banners || []} />
            </div>

            {/* iter146 — Group Categories rail was only ever rendered by
                the DEFAULT (Swiggy/Zomato-style) HomePage branch; any
                tenant on one of these marketplace presets (amazon /
                flipkart / blinkit — see HomePage.jsx's homeLayout
                switch) never got it at all, regardless of how many
                shop categories the admin mapped to a group. Renders
                nothing on its own when the tenant has no visible
                groups, so this is a no-op for tenants who never set
                Group Categories up. */}
            <GroupCategoryChipRow testIdPrefix={`mp-group-chips-${variant}`} />

            <CouponStrip coupons={feed?.coupons || []} variant={variant} />

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseHome />

            {sections === null ? (
                <div className="px-3 mt-4 grid grid-cols-2 gap-3" data-testid="mp-loading">
                    <CardSkeleton /><CardSkeleton />
                </div>
            ) : sections.length === 0 ? (
                <EmptySectionsHint />
            ) : (
                sections.map((s) => (
                    <SectionRow key={s.id} section={s} variant={variant} />
                ))
            )}

            <FooterTag tenant={tenant} />
        </div>
    );
}
